import { api } from '../../shared/api/client'
import { createIdempotencyKey } from '../layout-generator/api'

export interface ReleaseTarget {
  sellerId: number
  sellerName: string | null
  marketplace: string
  cabinet: string
  pathToken?: string
}

export interface ReleaseBlocker { code: string; message: string }
export interface ReleaseRow {
  article: string
  sellerId: number
  sellerName: string | null
  marketplace: string
  cardId: number | null
  cardStatus: string | null
  status: 'missing' | 'ready' | 'stale' | 'blocked' | 'failed' | 'processing' | 'published'
  slideCount: number
  sourceFingerprint: string
  blockers: ReleaseBlocker[]
  seoReady: boolean | null
  priceReady: boolean | null
  categoryReady: boolean | null
}

export interface ReleaseReadiness {
  targets: ReleaseTarget[]
  rows: ReleaseRow[]
  counts: Record<string, number>
}

export interface PreparationBatch {
  id: string
  status: 'processing' | 'done' | 'failed'
  rows: ReleaseRow[]
  counts: Record<string, number>
}

const unwrap = (response: any) => response.data?.data ?? response.data
const BASE = '/gingerbread/catalog'

export const releaseAPI = {
  key: () => createIdempotencyKey('gingerbread-preparation'),
  async readiness(articles: string[], sellerIds?: number[], signal?: AbortSignal): Promise<ReleaseReadiness> {
    return unwrap(await api.post(`${BASE}/marketplace-readiness`, { articles, sellerIds }, { signal }))
  },
  async prepare(articles: string[], sellerIds: number[], idempotencyKey: string, retryFailedOnly = false): Promise<PreparationBatch> {
    return unwrap(await api.post(`${BASE}/prepare`, { articles, sellerIds, idempotencyKey, retryFailedOnly }, {
      headers: { 'Idempotency-Key': idempotencyKey },
    }))
  },
  async batch(id: string, signal?: AbortSignal): Promise<PreparationBatch> {
    return unwrap(await api.get(`${BASE}/preparation/${encodeURIComponent(id)}`, { signal }))
  },
}

export function releaseError(error: any, fallback: string): string {
  return error?.response?.data?.error?.message || error?.message || fallback
}

export function saveBlob(data: BlobPart, name: string) {
  const href = URL.createObjectURL(new Blob([data]))
  const link = document.createElement('a')
  link.href = href
  link.download = name
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(href), 1000)
}
